<script setup lang="ts">
import ContactForm from '@/components/ContactForm.vue';
import LeasingCalculator from '@/components/LeasingCalculator.vue';
import RevealOnScroll from '@/components/RevealOnScroll.vue';
import SectionHeading from '@/components/SectionHeading.vue';

const steps = [
  { title: 'Оставляете заявку', text: 'Заполняете короткую форму на сайте или звоните нам.' },
  { title: 'Консультируем', text: 'Подбираем условия и присылаем предварительный график платежей.' },
  { title: 'Готовите документы', text: 'Собираете минимальный пакет документов, мы утверждаем сделку.' },
  { title: 'Заключаем договор', text: 'Подписываете договор лизинга и вносите авансовый платёж.' },
  { title: 'Получаете технику', text: 'Встречаемся для передачи предмета лизинга — и он ваш.' },
];
</script>

<template>
  <!-- Герой -->
  <section class="hero">
    <div class="container grid grid-hero">
      <RevealOnScroll>
        <span class="badge">Специальное предложение</span>
        <h1 class="hero-title">Первый взнос 0% на 72 месяца при ставке от 13,5% в BYN</h1>
        <p class="section-lead">
          Лизинг легковых автомобилей, коммерческого транспорта и спецтехники для
          физических и юридических лиц. Решение по заявке — в течение суток.
        </p>
        <div class="hero-actions">
          <a href="#zayavka" class="btn btn-primary btn-lg">Оставить заявку</a>
          <a href="#calculator" class="btn btn-secondary btn-lg">Рассчитать платёж</a>
        </div>
      </RevealOnScroll>

      <RevealOnScroll>
        <div class="card card-lg">
          <h3 class="rates-card__title">Актуальные ставки лизинга</h3>
          <div class="rate-list">
            <div class="rate-row">
              <span class="rate-row__label">Легковые автомобили</span>
              <strong>от 13,5%</strong>
            </div>
            <div class="rate-row">
              <span class="rate-row__label">Коммерческий транспорт</span>
              <strong>от 14,2%</strong>
            </div>
            <div class="rate-row">
              <span class="rate-row__label">Спецтехника</span>
              <strong>от 14,8%</strong>
            </div>
          </div>
          <p class="field-hint rates-card__hint">
            Ставки указаны в белорусских рублях (BYN) и зависят от срока договора и суммы аванса.
          </p>
        </div>
      </RevealOnScroll>
    </div>
  </section>

  <!-- Полоса доверия -->
  <section class="trust-bar">
    <div class="container trust-list">
      <div class="trust-item">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2">
          <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z" />
        </svg>
        <span class="trust-item__text">В реестре лизинговых организаций НБ РБ</span>
      </div>
      <div class="trust-item">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 3" />
        </svg>
        <span class="trust-item__text">Решение по заявке за 24 часа</span>
      </div>
      <div class="trust-item">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2">
          <path d="M20 7h-9M14 17H5M17 3v8M7 13v8" />
          <circle cx="17" cy="7" r="2" />
          <circle cx="7" cy="17" r="2" />
        </svg>
        <span class="trust-item__text">Гибкий индивидуальный график платежей</span>
      </div>
    </div>
  </section>

  <!-- Предметы лизинга -->
  <section>
    <div class="container">
      <SectionHeading
        eyebrow="Предметы лизинга"
        title="Финансируем то, что нужно вашему бизнесу и вашей семье"
        lead="Работаем с физическими и юридическими лицами: от личного автомобиля до обновления коммерческого автопарка. Для физических лиц и юридических лиц (ИП) применяется финансовый лизинг."
      />
      <div class="grid grid-cols-3">
        <RevealOnScroll class="card">
          <svg class="icon-lg" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="1.8">
            <path d="M3 13l1.5-5A2 2 0 0 1 6.4 6.5h11.2A2 2 0 0 1 19.5 8L21 13v6a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6z" />
            <circle cx="7.5" cy="16.5" r="1.5" />
            <circle cx="16.5" cy="16.5" r="1.5" />
          </svg>
          <h3 class="card__title">Легковые автомобили</h3>
          <p class="card__text">
            Для физических и юридических лиц. Новые и с пробегом, аванс от 0%, срок до 72 месяцев.
          </p>
        </RevealOnScroll>

        <RevealOnScroll class="card">
          <svg class="icon-lg" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="1.8">
            <rect x="1" y="9" width="14" height="8" rx="1" />
            <path d="M15 11h4l3 3v3h-7z" />
            <circle cx="6" cy="19" r="1.6" />
            <circle cx="17" cy="19" r="1.6" />
          </svg>
          <h3 class="card__title">Коммерческий транспорт</h3>
          <p class="card__text">
            Грузовые автомобили, фургоны, автобусы для обновления автопарка и логистики бизнеса.
          </p>
        </RevealOnScroll>

        <RevealOnScroll class="card">
          <svg class="icon-lg" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="1.8">
            <path d="M4 16l3-8h6l3 8" />
            <circle cx="8" cy="18" r="1.6" />
            <circle cx="16" cy="18" r="1.6" />
            <path d="M13 8V4h4l3 4" />
          </svg>
          <h3 class="card__title">Спецтехника</h3>
          <p class="card__text">
            Строительная и дорожная техника, оборудование для производства и сельского хозяйства.
          </p>
        </RevealOnScroll>
      </div>
    </div>
  </section>

  <!-- Как получить лизинг -->
  <section class="section-tinted">
    <div class="container">
      <SectionHeading eyebrow="Как получить лизинг" title="Оформление в пять шагов" />
      <div class="grid grid-cols-5 steps-grid">
        <RevealOnScroll v-for="(step, index) in steps" :key="step.title" class="step">
          <div class="step-num">0{{ index + 1 }}</div>
          <h4 class="step__title">{{ step.title }}</h4>
          <p class="step__text">{{ step.text }}</p>
        </RevealOnScroll>
      </div>
    </div>
  </section>

  <!-- Калькулятор -->
  <LeasingCalculator />

  <!-- Преимущества -->
  <section class="section-tinted">
    <div class="container">
      <SectionHeading eyebrow="Наши преимущества" title="Почему выбирают «Т-Капитал»" />
      <div class="grid grid-cols-4">
        <RevealOnScroll class="advantage">
          <svg class="advantage__icon" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="1.8">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 3" />
          </svg>
          <h4 class="advantage__title">Решение за 24 часа</h4>
          <p class="advantage__text">Предварительное одобрение заявки — в течение одного рабочего дня.</p>
        </RevealOnScroll>

        <RevealOnScroll class="advantage">
          <svg class="advantage__icon" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="1.8">
            <path d="M4 6h16M4 12h16M4 18h10" />
          </svg>
          <h4 class="advantage__title">Понятные условия</h4>
          <p class="advantage__text">Прозрачный график платежей без скрытых комиссий.</p>
        </RevealOnScroll>

        <RevealOnScroll class="advantage">
          <svg class="advantage__icon" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="1.8">
            <path d="M20 7h-9M14 17H5M17 3v8M7 13v8" />
            <circle cx="17" cy="7" r="2" />
            <circle cx="7" cy="17" r="2" />
          </svg>
          <h4 class="advantage__title">Гибкий график</h4>
          <p class="advantage__text">Индивидуальный график с учётом сезонности бизнеса.</p>
        </RevealOnScroll>

        <RevealOnScroll class="advantage">
          <svg class="advantage__icon" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="1.8">
            <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z" />
          </svg>
          <h4 class="advantage__title">Профессиональная экспертиза</h4>
          <p class="advantage__text">Многолетний опыт в лизинге легковых, коммерческих и специальных объектов.</p>
        </RevealOnScroll>
      </div>
    </div>
  </section>

  <!-- Заявка -->
  <section id="zayavka">
    <div class="container-narrow">
      <SectionHeading
        eyebrow="Заявка"
        title="Узнать индивидуальные условия"
        lead="Оставьте контакты — наш специалист подберёт условия лизинга под ваш случай и свяжется с вами."
      />
      <ContactForm id-prefix="home" show-who />
    </div>
  </section>
</template>
